// src/components/settings/AppConfigModal.js
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import styles from './AppConfigModal.module.css';
import Modal from '../common/Modal';
import StatusToggle from '../common/StatusToggle';
import {
    CONFIG_PLATFORMS,
    createAppConfig,
    MAX_GROUP_LENGTH,
    MAX_KEY_LENGTH,
    readApiError,
    toIdentifier,
    updateAppConfig,
    validateIdentifier,
    validateValue,
    VALUE_TYPES,
} from '../../services/appConfigApi';

const EMPTY_FORM = {
    group: '',
    key: '',
    value: '',
    valueType: 'STRING',
    platform: 'all',
    description: '',
    enabled: true,
};

const toForm = (entry, defaultGroup) =>
    entry
        ? {
            group: entry.group || '',
            key: entry.key || '',
            value: entry.value ?? '',
            valueType: entry.valueType || 'STRING',
            platform: entry.platform || 'all',
            // Gson null alanları yanıttan düşürüyor: açıklama boşsa alan hiç gelmez.
            description: entry.description ?? '',
            enabled: entry.enabled !== false,
        }
        : { ...EMPTY_FORM, group: defaultGroup || '' };

// Tip değişince eski değer yeni tipe uymayabilir; BOOL'da anahtar boş değer
// taşıyamayacağı için bilinen bir değere çekiyoruz.
const coerceValueForType = (value, valueType) => {
    if (valueType === 'BOOL') return value === 'true' ? 'true' : 'false';
    return value;
};

const PLATFORM_NOTES = {
    all: 'iOS ve Android kullanıcılarının hepsi bu ayarı alır.',
    ios: 'Yalnızca iOS uygulaması bu anahtarı görür; Android yanıtında hiç yer almaz.',
    android: 'Yalnızca Android uygulaması bu anahtarı görür; iOS yanıtında hiç yer almaz.',
};

/**
 * Uzaktan konfigürasyon kaydı oluşturma / düzenleme.
 *
 * Düzenlemede group ve key salt okunur: sunucu onları yok sayar, çünkü yeniden
 * adlandırmak eski istemciler için silmekle aynı şey.
 *
 * `entries` 409 durumunda mevcut kaydı bulup düzenlemeye yönlendirmek ve grup
 * önerileri için kullanılır.
 */
const AppConfigModal = ({ isOpen, entry, defaultGroup, groups = [], entries = [], onClose, onSaved, onEditExisting }) => {
    const isEdit = !!entry;
    const [form, setForm] = useState(() => toForm(entry, defaultGroup));
    const [showErrors, setShowErrors] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [conflict, setConflict] = useState(null);

    // Modal her açılışta seçilen kayıtla (ya da boş formla) başlar.
    useEffect(() => {
        if (!isOpen) return;
        setForm(toForm(entry, defaultGroup));
        setShowErrors(false);
        setConflict(null);
    }, [isOpen, entry, defaultGroup]);

    const saved = useMemo(() => toForm(entry, defaultGroup), [entry, defaultGroup]);

    const errors = useMemo(() => {
        const next = {};
        if (!isEdit) {
            const groupError = validateIdentifier(form.group, { label: 'Grup', max: MAX_GROUP_LENGTH });
            const keyError = validateIdentifier(form.key, { label: 'Anahtar', max: MAX_KEY_LENGTH });
            if (groupError) next.group = groupError;
            if (keyError) next.key = keyError;
        }
        const valueError = validateValue(form.value, form.valueType);
        if (valueError) next.value = valueError;
        return next;
    }, [form, isEdit]);

    const hasErrors = Object.keys(errors).length > 0;
    const typeChanged = isEdit && form.valueType !== saved.valueType;

    // Düzenlemede yalnızca değişen alanlar gönderilir; göndermediğimiz alana
    // sunucu dokunmaz.
    const patch = useMemo(() => {
        if (!isEdit) return null;
        const next = {};
        ['value', 'valueType', 'platform', 'description'].forEach((field) => {
            if (form[field] !== saved[field]) next[field] = form[field];
        });
        return next;
    }, [form, saved, isEdit]);

    const isDirty = isEdit ? Object.keys(patch).length > 0 : true;

    const setField = (field, value) => {
        setConflict(null);
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleTypeChange = (valueType) => {
        setConflict(null);
        setForm((prev) => ({ ...prev, valueType, value: coerceValueForType(prev.value, valueType) }));
    };

    const handleFormatJson = () => {
        try {
            setField('value', JSON.stringify(JSON.parse(form.value), null, 2));
        } catch {
            setShowErrors(true);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (hasErrors) {
            setShowErrors(true);
            return;
        }
        if (isEdit && !isDirty) {
            onClose();
            return;
        }

        setIsSaving(true);
        try {
            let result;
            if (isEdit) {
                result = await updateAppConfig(entry.id, patch);
                toast.success(`${entry.group}.${entry.key} güncellendi.`);
            } else {
                const payload = {
                    group: form.group,
                    key: form.key,
                    value: form.value,
                    valueType: form.valueType,
                    platform: form.platform,
                    enabled: form.enabled,
                };
                if (form.description.trim()) payload.description = form.description.trim();
                result = await createAppConfig(payload);
                toast.success(`${form.group}.${form.key} oluşturuldu.`);
            }
            onSaved?.(result, { group: form.group });
        } catch (error) {
            const { code, message } = readApiError(error, 'Ayar kaydedilemedi.');
            if (code === 'CONFIG_KEY_EXISTS') {
                const existing = entries.find((e) => e.group === form.group && e.key === form.key);
                setConflict({ message, existing });
            } else {
                toast.error(message);
            }
        } finally {
            setIsSaving(false);
        }
    };

    const fieldClass = (field, base = styles.input) =>
        `${base} ${showErrors && errors[field] ? styles.inputError : ''}`;

    const typeMeta = VALUE_TYPES.find((t) => t.value === form.valueType);

    const renderValueEditor = () => {
        switch (form.valueType) {
            case 'BOOL':
                return (
                    <div className={styles.boolRow}>
                        <StatusToggle
                            label="Değer"
                            checked={form.value === 'true'}
                            disabled={isSaving}
                            onChange={(e) => setField('value', e.target.checked ? 'true' : 'false')}
                            onText="true"
                            offText="false"
                        />
                    </div>
                );
            case 'INT':
            case 'DOUBLE':
                return (
                    <input
                        id="config-value"
                        type="text"
                        inputMode={form.valueType === 'INT' ? 'numeric' : 'decimal'}
                        className={fieldClass('value')}
                        placeholder={form.valueType === 'INT' ? '9' : '1.5'}
                        value={form.value}
                        onChange={(e) => setField('value', e.target.value)}
                        disabled={isSaving}
                    />
                );
            case 'JSON':
                return (
                    <>
                        <textarea
                            id="config-value"
                            className={fieldClass('value', `${styles.input} ${styles.textarea} ${styles.code}`)}
                            placeholder={'{\n  "enabled": true\n}'}
                            rows={8}
                            spellCheck={false}
                            value={form.value}
                            onChange={(e) => setField('value', e.target.value)}
                            disabled={isSaving}
                        />
                        <div className={styles.inlineActions}>
                            <button
                                type="button"
                                className={styles.linkButton}
                                onClick={handleFormatJson}
                                disabled={isSaving || !form.value.trim()}
                            >
                                Biçimlendir
                            </button>
                        </div>
                    </>
                );
            default:
                return (
                    <textarea
                        id="config-value"
                        className={fieldClass('value', `${styles.input} ${styles.textarea}`)}
                        placeholder="destek@utkanesim.com"
                        rows={2}
                        value={form.value}
                        onChange={(e) => setField('value', e.target.value)}
                        disabled={isSaving}
                    />
                );
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={isSaving ? () => {} : onClose}
            title={isEdit ? `${entry.group}.${entry.key}` : 'Yeni ayar'}
        >
            <form className={styles.form} onSubmit={handleSubmit} noValidate>
                {isEdit ? (
                    <div className={styles.identity}>
                        <span className={styles.identityPath}>
                            {entry.group}.<strong>{entry.key}</strong>
                        </span>
                        <span className={styles.identityNote}>
                            Grup ve anahtar değiştirilemez. Yeniden adlandırmak için yeni kayıt açıp bunu yayından kaldırın.
                        </span>
                    </div>
                ) : (
                    <div className={styles.row}>
                        <div className={styles.field}>
                            <label className={styles.label} htmlFor="config-group">Grup</label>
                            <input
                                id="config-group"
                                type="text"
                                list="config-group-options"
                                className={fieldClass('group', `${styles.input} ${styles.code}`)}
                                placeholder="support"
                                value={form.group}
                                maxLength={MAX_GROUP_LENGTH}
                                onChange={(e) => setField('group', toIdentifier(e.target.value))}
                                disabled={isSaving}
                                autoComplete="off"
                            />
                            <datalist id="config-group-options">
                                {groups.map((g) => <option key={g} value={g} />)}
                            </datalist>
                            {showErrors && errors.group ? (
                                <span className={styles.errorText}>{errors.group}</span>
                            ) : (
                                <span className={styles.hint}>Mevcut bir grubu seçin ya da yenisini yazın.</span>
                            )}
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label} htmlFor="config-key">Anahtar</label>
                            <input
                                id="config-key"
                                type="text"
                                className={fieldClass('key', `${styles.input} ${styles.code}`)}
                                placeholder="whatsapp_url"
                                value={form.key}
                                maxLength={MAX_KEY_LENGTH}
                                onChange={(e) => setField('key', toIdentifier(e.target.value))}
                                disabled={isSaving}
                                autoComplete="off"
                            />
                            {showErrors && errors.key ? (
                                <span className={styles.errorText}>{errors.key}</span>
                            ) : (
                                <span className={styles.hint}>Küçük harf, rakam ve alt çizgi. Boşluklar _ olur.</span>
                            )}
                        </div>
                    </div>
                )}

                <div className={styles.row}>
                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="config-type">Tip</label>
                        <select
                            id="config-type"
                            className={styles.input}
                            value={form.valueType}
                            onChange={(e) => handleTypeChange(e.target.value)}
                            disabled={isSaving}
                        >
                            {VALUE_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>
                                    {t.label === t.value ? t.value : `${t.value} — ${t.label}`}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="config-platform">Platform</label>
                        <select
                            id="config-platform"
                            className={styles.input}
                            value={form.platform}
                            onChange={(e) => setField('platform', e.target.value)}
                            disabled={isSaving}
                        >
                            {CONFIG_PLATFORMS.map((p) => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <p className={form.platform === 'all' ? styles.hint : styles.notice}>
                    {PLATFORM_NOTES[form.platform]}
                </p>

                {typeChanged && (
                    <div className={styles.warning}>
                        Tip <strong>{saved.valueType}</strong> → <strong>{form.valueType}</strong> değişiyor. Yayındaki
                        uygulama sürümleri bu değeri okuyamayabilir; zorunlu değilse tipi sabit tutun, gerekiyorsa
                        mobil ekiple konuşun.
                    </div>
                )}

                <div className={styles.field}>
                    <label className={styles.label} htmlFor="config-value">Değer</label>
                    {renderValueEditor()}
                    {showErrors && errors.value ? (
                        <span className={styles.errorText}>{errors.value}</span>
                    ) : (
                        typeMeta && form.valueType !== 'BOOL' && <span className={styles.hint}>{typeMeta.hint}</span>
                    )}
                </div>

                <div className={styles.field}>
                    <label className={styles.label} htmlFor="config-description">Açıklama</label>
                    <textarea
                        id="config-description"
                        className={`${styles.input} ${styles.textarea}`}
                        rows={2}
                        placeholder="Bu ayar ne yapar? Örn. Destek ekranındaki WhatsApp bağlantısı"
                        value={form.description}
                        onChange={(e) => setField('description', e.target.value)}
                        disabled={isSaving}
                    />
                    <span className={styles.hint}>Yalnızca panelde görünür, mobil yanıta girmez. Gizli bilgi yazmayın.</span>
                </div>

                {!isEdit && (
                    <div className={styles.field}>
                        <StatusToggle
                            label="Yayın durumu"
                            checked={form.enabled}
                            disabled={isSaving}
                            onChange={(e) => setField('enabled', e.target.checked)}
                            onText="Kaydedince yayına girer"
                            offText="Pasif olarak kaydedilir"
                        />
                    </div>
                )}

                {conflict && (
                    <div className={styles.conflict}>
                        <span>{conflict.message}</span>
                        {conflict.existing && onEditExisting && (
                            <button
                                type="button"
                                className={styles.linkButton}
                                onClick={() => onEditExisting(conflict.existing)}
                            >
                                Mevcut kaydı düzenle
                            </button>
                        )}
                    </div>
                )}

                <div className={styles.footer}>
                    <span className={styles.footerNote}>Kaydettiğiniz an yayına girer; kullanıcı uygulamayı bir sonraki açışında alır.</span>
                    <div className={styles.actions}>
                        <button type="button" className={styles.ghostButton} onClick={onClose} disabled={isSaving}>
                            Vazgeç
                        </button>
                        <button type="submit" className={styles.saveButton} disabled={isSaving || (isEdit && !isDirty)}>
                            {isSaving ? 'Kaydediliyor…' : isEdit ? 'Kaydet' : 'Oluştur'}
                        </button>
                    </div>
                </div>
            </form>
        </Modal>
    );
};

export default AppConfigModal;
